import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { COUNTRY_CODES } from "../lib/countryCodes";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, ArrowRight, EnvelopeSimple, DeviceMobile } from "@phosphor-icons/react";

function errText(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").join(" ");
  return String(detail);
}

export default function AccountRecovery() {
  const [method, setMethod] = useState("email");
  return (
    <div className="min-h-screen bg-ink flex items-center justify-center px-5 py-10 grain">
      <div className="relative z-10 w-full max-w-md crystal-glass rounded-3xl p-8 sm:p-10 animate-fade-up">
        <Link to="/login" data-testid="recover-back" className="inline-flex items-center gap-2 text-cream/60 hover:text-cream text-xs tracking-[0.2em] uppercase font-bold mb-4">
          <ArrowLeft size={14} /> Back to login
        </Link>
        <p className="text-papaya text-xs tracking-[0.3em] uppercase font-bold">Account recovery</p>
        <h1 className="text-cream text-4xl font-black tracking-tighter mt-1 leading-none">Forgot your<br />password?</h1>
        <p className="text-cream/60 mt-3 text-sm">Enter the email address or phone number you registered with.</p>

        <div className="mt-6 grid grid-cols-2 gap-2 p-1 rounded-2xl bg-white/5">
          <button
            onClick={() => setMethod("email")}
            data-testid="recover-tab-email"
            className={`py-2.5 rounded-xl text-xs font-black tracking-wide uppercase flex items-center justify-center gap-2 transition-colors ${method === "email" ? "bg-papaya text-ink" : "text-cream/70"}`}
          >
            <EnvelopeSimple size={16} weight="fill" /> Email
          </button>
          <button
            onClick={() => setMethod("phone")}
            data-testid="recover-tab-phone"
            className={`py-2.5 rounded-xl text-xs font-black tracking-wide uppercase flex items-center justify-center gap-2 transition-colors ${method === "phone" ? "bg-papaya text-ink" : "text-cream/70"}`}
          >
            <DeviceMobile size={16} weight="fill" /> Phone
          </button>
        </div>

        {method === "email" ? <EmailReset /> : <PhoneReset />}

        <p className="mt-6 text-cream/50 text-xs text-center">
          Forgot your email or phone too?{" "}
          <Link to="/recover-id" data-testid="recover-to-identifier" className="text-papaya font-bold hover:underline">Recover account</Link>
        </p>
      </div>
    </div>
  );
}

function EmailReset() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [debugUrl, setDebugUrl] = useState("");
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(""); setDebugUrl("");
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setDone(true);
      if (data.debug_reset_url) setDebugUrl(data.debug_reset_url);
    } catch (e) {
      setErr(errText(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="mt-8" data-testid="recover-email-done">
        <div className="rounded-2xl bg-jam-green/15 border border-jam-green/30 p-4">
          <p className="text-cream text-sm">Check your email. If an account is associated with the information provided, we sent instructions to reset your password.</p>
        </div>
        {debugUrl && (
          <a href={debugUrl} data-testid="recover-debug-url" className="mt-3 block text-papaya text-xs break-all hover:underline">QA link: {debugUrl}</a>
        )}
        <Link to="/login" className="mt-5 block text-center text-cream/60 text-xs tracking-[0.2em] uppercase hover:text-papaya">Return to login</Link>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="mt-8 space-y-4">
      <label className="block">
        <span className="text-cream/70 text-xs tracking-[0.2em] uppercase font-bold">Email address</span>
        <input data-testid="recover-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
          className="mt-2 w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-cream focus:outline-none focus:border-papaya" />
      </label>
      {err && <p data-testid="recover-email-error" className="text-red-400 text-sm">{err}</p>}
      <button data-testid="recover-email-submit" type="submit" disabled={busy}
        className="w-full py-4 rounded-full bg-papaya text-ink font-black tracking-[0.2em] uppercase text-sm hover:bg-papaya-hover transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
        {busy ? "Sending…" : <>Send reset link <ArrowRight size={16} weight="bold" /></>}
      </button>
    </form>
  );
}

function PhoneReset() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [cc, setCc] = useState(0);
  const [number, setNumber] = useState("");
  const [step, setStep] = useState("number");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const fullPhone = () => `${COUNTRY_CODES[cc].code}${number.replace(/[^0-9]/g, "")}`;

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const send = async (e) => {
    e?.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/recover/phone/send", { phone: fullPhone() });
      setStep("code");
      setCooldown(data?.resend_in || 30);
    } catch (e) { setErr(errText(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/recover/phone/verify", { phone: fullPhone(), code });
      await refresh();
      nav("/reset-password", { state: { token: data.reset_token, viaPhone: true } });
    } catch (e) { setErr(errText(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (step === "number") {
    return (
      <form onSubmit={send} className="mt-8 space-y-4">
        <span className="text-cream/70 text-xs tracking-[0.2em] uppercase font-bold">Phone number</span>
        <div className="flex gap-2 mt-2">
          <select data-testid="recover-country" value={cc} onChange={(e) => setCc(Number(e.target.value))}
            className="bg-white/5 border border-white/10 rounded-2xl px-2 py-3 text-cream text-sm focus:outline-none focus:border-papaya max-w-[42%]">
            {COUNTRY_CODES.map((c, i) => (
              <option key={`${c.name}${i}`} value={i} className="bg-ink">{c.flag} {c.code} {c.name}</option>
            ))}
          </select>
          <input data-testid="recover-phone-input" type="tel" required value={number} onChange={(e) => setNumber(e.target.value)} placeholder="876 555 1234"
            className="flex-1 bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-cream focus:outline-none focus:border-papaya" />
        </div>
        <p className="text-cream/40 text-xs">We'll text a verification code to your registered number.</p>
        {err && <p data-testid="recover-phone-error" className="text-red-400 text-sm">{err}</p>}
        <button data-testid="recover-phone-send" type="submit" disabled={busy}
          className="w-full py-4 rounded-full bg-papaya text-ink font-black tracking-[0.2em] uppercase text-sm hover:bg-papaya-hover transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
          {busy ? "Sending…" : <>Send code <ArrowRight size={16} weight="bold" /></>}
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={verify} className="mt-8 space-y-4">
      <button type="button" data-testid="recover-phone-back" onClick={() => { setStep("number"); setCode(""); setErr(""); }}
        className="text-cream/50 text-xs flex items-center gap-1 hover:text-papaya"><ArrowLeft size={14} /> Change number</button>
      <p className="text-cream/70 text-sm">Enter the code sent to <span className="text-papaya font-bold">{fullPhone()}</span>.</p>
      <input data-testid="recover-otp-input" value={code} onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ""))} inputMode="numeric" placeholder="123456"
        className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-cream focus:outline-none focus:border-papaya" />
      {err && <p data-testid="recover-otp-error" className="text-red-400 text-sm">{err}</p>}
      <button data-testid="recover-otp-verify" type="submit" disabled={busy}
        className="w-full py-4 rounded-full bg-papaya text-ink font-black tracking-[0.2em] uppercase text-sm hover:bg-papaya-hover transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
        {busy ? "Verifying…" : <>Verify <ArrowRight size={16} weight="bold" /></>}
      </button>
      <button type="button" data-testid="recover-otp-resend" onClick={send} disabled={busy || cooldown > 0}
        className="w-full text-center text-cream/60 text-xs hover:text-papaya disabled:opacity-40">
        {cooldown > 0 ? `Resend code in ${cooldown}s` : "Didn't get it? Resend code"}
      </button>
    </form>
  );
}
